// European phone number lengths by country code
// DOMESTIC — what users usually type locally (incl. trunk ‘0’ where used)
export const euPhoneLengthsDomestic: Record<string, number | number[]> = {
    "+33": [9, 10],
    "+44": 11,
    "+39": [6, 11],
    "+43": [5, 14],
    "+49": [6, 12],
    "+31": 10,
    "+41": 9,
    "+34": 9,
    "+45": 8,
    "+47": 8,
    "+48": 9,
    "+351": 9,
    "+46": [8, 11],
    "+420": 9,
    "+36": 9,
    "+354": 7,
    "+353": [8, 10],
    "+32": [9, 10],
    "+371": 8,
    "+370": 8,
    "+352": [4, 11],
    "+389": 8,
    "+356": 8,
    "+373": 8,
    "+377": 8,
    "+382": 8,
    "+357": 8,
    "+385": 9,
    "+381": 9,
    "+421": 9,
    "+386": 8,
    "+40": 9,
    "+359": 9,
    "+387": 8,
    "+372": [7, 8],
    "+358": [6, 13],
    "+375": 9,
    "+380": 9,
    "+7": 10,
    "+30": 10,
    "+90": 10,
    // French overseas territories (10-digit domestic format, same as +33)
    "+262": 10,
    "+590": 10,
    "+594": 10,
    "+596": 10,
    "+681": 10,
    "+687": 10,
    "+689": 10,
}

const NL_DOMESTIC_PHONE_CODE = '+31'
const NL_DOMESTIC_PHONE_PATTERN = /^0\d{9}$/

export type PhoneLengthRule = {
    lengths: number[]
    requiresLeadingZero: boolean
}

export function getPhoneLengthRule(phoneCode: string): PhoneLengthRule | null {
    if (phoneCode === NL_DOMESTIC_PHONE_CODE) {
        return { lengths: [10], requiresLeadingZero: true }
    }

    const validLengths = euPhoneLengthsDomestic[phoneCode]
    if (!validLengths) return null

    const lengths = Array.isArray(validLengths) ? validLengths : [validLengths]
    return { lengths, requiresLeadingZero: false }
}

type TranslateFn = (key: string, params?: Record<string, number>) => string

/** Error copy that matches the length / leading-0 rule for the selected dial code. */
export function getInvalidPhoneMessage(t: TranslateFn, phoneCode: string): string {
    const rule = getPhoneLengthRule(phoneCode)
    if (!rule) return t('loginRegister.user_info_invalid_phone')

    if (rule.requiresLeadingZero) {
        return t('loginRegister.user_info_invalid_phone_length_start_zero', {
            count: rule.lengths[0],
        })
    }

    if (rule.lengths.length === 1) {
        return t('loginRegister.user_info_invalid_phone_length', {
            count: rule.lengths[0],
        })
    }

    return t('loginRegister.user_info_invalid_phone_length_range', {
        min: Math.min(...rule.lengths),
        max: Math.max(...rule.lengths),
    })
}

/**
 * NL only: users type domestic numbers in phone_number (+31 in phone_code).
 * libphonenumber accepts 9-digit input without trunk 0 (e.g. 683581408), but the BE expects 10 digits starting with 0.
 */
export function passesDomesticPhoneCheck(phoneCode: string, phoneNumber: string): boolean {
    if (phoneCode !== NL_DOMESTIC_PHONE_CODE) return true
    return NL_DOMESTIC_PHONE_PATTERN.test((phoneNumber || '').trim())
}

/** Length-based validation aligned with single-account / booking-funnel. NL also requires leading 0. */
export function isPhoneNumberValid(phoneNumber: string, phoneCode: string): boolean {
    const trimmed = (phoneNumber || '').trim()
    if (!trimmed) return false

    if (phoneCode === NL_DOMESTIC_PHONE_CODE) {
        return NL_DOMESTIC_PHONE_PATTERN.test(trimmed)
    }

    const validLengths = euPhoneLengthsDomestic[phoneCode]
    if (!validLengths) return trimmed.length > 0

    const lengths = Array.isArray(validLengths) ? validLengths : [validLengths]
    return lengths.includes(trimmed.length)
}
